import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { IconButton } from 'reaxl';

import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';

import useMediaGalleryAnalytics from './useMediaGalleryAnalytics';

const TOUCHING_DRAGGING_THRESHOLD = 50;

const keyCode = {
    TAB: 9,
    ESC: 27,
    RIGHT_ARROW: 39,
    LEFT_ARROW: 37,
};

const getTouchPosition = (e) => ({
    x: e.touches[0].clientX,
    y: e.touches[0].clientY,
});

function FullscreenViewer({
    isMobile,
    headerCtaContent,
    closeFullScreen,
    fullscreenDataSet,
    defaultIndex,
    onStateUpdateCallback,
    isPsxVdp,
    relocateCTAs,
}) {
    const sendAnalyticsEvent = useMediaGalleryAnalytics();
    const isConfirmModalShowing = useSelector(vdpActiveInteractionDuck.selectors.getShowEmailModal);
    const [currentIndex, setCurrentIndex] = useState(defaultIndex);
    const touchTracking = useRef({});
    const fullscreenContainer = useRef(null);

    const getPreviousItem = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }, [currentIndex]);

    const getNextItem = useCallback(() => {
        if (currentIndex < fullscreenDataSet.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }, [currentIndex, fullscreenDataSet.length]);

    const handleFocusTrapNavigation = (shiftKey, focusableItems) => {
        const trapped = focusableItems.includes(document.activeElement);
        const lastItem = focusableItems[focusableItems.length - 1];
        const firstItem = focusableItems[0];
        const currentTabIndex = focusableItems.indexOf(document.activeElement);

        if (shiftKey) {
            if (document.activeElement === firstItem || !trapped) {
                lastItem.focus();
            } else {
                focusableItems[currentTabIndex - 1].focus();
            }
        } else if (document.activeElement === lastItem || !trapped) {
            firstItem.focus();
        } else {
            focusableItems[currentTabIndex + 1].focus();
        }
    };

    const handleKeyDown = useCallback((e) => {
        if (e.keyCode === keyCode.TAB) {
            const fullscreenViewer = fullscreenContainer.current;
            const focusableItems = Array.from(fullscreenViewer.querySelectorAll('[data-cmp="fullscreen-action-bar"] button, .control-button-container button:enabled'));
            const currentViewFocusableItems = Array.from(fullscreenViewer.querySelector('.image-carousel').children[currentIndex].querySelectorAll('button, [tabindex]'));
            focusableItems.push(...currentViewFocusableItems);
            if (!currentViewFocusableItems.includes(document.activeElement)) {
                handleFocusTrapNavigation(e.shiftKey, focusableItems);
                e.preventDefault();
            }
        } else if ([keyCode.ESC, keyCode.LEFT_ARROW, keyCode.RIGHT_ARROW].includes(e.keyCode)) {
            ({
                [keyCode.ESC]: () => closeFullScreen(),
                [keyCode.LEFT_ARROW]: () => getPreviousItem(),
                [keyCode.RIGHT_ARROW]: () => getNextItem(),
            }[e.keyCode])();
        }
    }, [closeFullScreen, currentIndex, getNextItem, getPreviousItem]);

    useEffect(() => {
        onStateUpdateCallback(currentIndex);
    }, [currentIndex, onStateUpdateCallback]);

    useEffect(() => {
        // only enable focus trap if the confirmation availability modal is not open
        if (isConfirmModalShowing) {
            return () => {};
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [closeFullScreen, getPreviousItem, getNextItem, currentIndex, isConfirmModalShowing, handleKeyDown]);

    const handleTouchStart = (e) => {
        const { x, y } = getTouchPosition(e);
        touchTracking.current = {
            startX: x,
            startY: y,
            endX: x,
            endY: y,
        };
    };

    const handleTouchEnd = () => {
        const { startX, endX } = touchTracking.current;
        if (Math.abs(startX - endX) > TOUCHING_DRAGGING_THRESHOLD) {
            if (startX > endX) {
                getNextItem();
            } else {
                getPreviousItem();
            }
        }
    };

    const handleTouchMove = (e) => {
        const { x, y } = getTouchPosition(e);
        touchTracking.current = {
            ...touchTracking.current,
            endX: x,
            endY: y,
        };
    };

    const handleBackButtonClick = useCallback(() => {
        closeFullScreen();
        if (!isPsxVdp) {
            sendAnalyticsEvent('click', 'viewAllMediaClick');
        }
    }, [closeFullScreen, isPsxVdp, sendAnalyticsEvent]);

    return (
        <div
            data-cmp="fullscreenViewer"
            ref={fullscreenContainer}
            className="inverted display-flex align-items-center w-100 h-100 top-0 left-0"
        >
            <div
                className="action-bar position-absolute top-0 display-flex padding-2 w-100"
                data-cmp="fullscreen-action-bar"
            >
                <IconButton
                    label="Back"
                    glyph="chevron-left"
                    monochrome
                    hideLabel={isMobile}
                    onClick={handleBackButtonClick}
                />
                {!relocateCTAs && headerCtaContent}
            </div>
            <div
                className="image-carousel display-grid position-absolute w-100 h-100"
                style={{
                    left: `-${currentIndex * 100}vw`,
                }}
            >
                {fullscreenDataSet.map(({ element }, i) => (
                    <div
                        key={`fullscreen-item-${i}`}
                        className="carousel-image-wrapper display-flex justify-content-center align-items-center"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        onTouchMove={handleTouchMove}
                    >
                        {element}
                    </div>
                ))}
            </div>
            {!isMobile && (
                <div
                    className="control-button-container w-100 display-flex justify-content-between padding-5"
                >
                    <IconButton
                        aria-label="Previous Image"
                        disabled={currentIndex < 1}
                        onClick={getPreviousItem}
                        glyph="chevron-left"
                        hideLabel
                        monochrome
                        size="medium"
                    />
                    <IconButton
                        aria-label="Next Image"
                        disabled={currentIndex > fullscreenDataSet.length - 2}
                        onClick={getNextItem}
                        glyph="chevron-right"
                        hideLabel
                        monochrome
                        size="medium"
                    />
                </div>
            )}
            <div
                className={clsx({ 'padding-bottom-5': !relocateCTAs }, 'carousel-indicator position-absolute bottom-0 w-100 text-center text-bold')}
            >
                {currentIndex + 1}
                {' '}
                of
                {' '}
                {fullscreenDataSet.length}
                { relocateCTAs && headerCtaContent}
            </div>
        </div>
    );
}

FullscreenViewer.propTypes = {
    isMobile: PropTypes.bool,
    headerCtaContent: PropTypes.element,
    show: PropTypes.bool,
    closeFullScreen: PropTypes.func,
    fullscreenDataSet: PropTypes.array,
    defaultIndex: PropTypes.number,
    onStateUpdateCallback: PropTypes.func,
    isPsxVdp: PropTypes.bool,
};

FullscreenViewer.defaultProps = {
    isMobile: false,
    headerCtaContent: null,
    show: false,
    closeFullScreen: () => { },
    fullscreenDataSet: [],
    defaultIndex: 0,
    onStateUpdateCallback: () => { },
    isPsxVdp: false,
};

export default FullscreenViewer;
